/**
 * `network/curl-convert` — cURL ↔ fetch / axios / Python requests /
 * wget converter.
 *
 * The hard half is parsing cURL's CLI shape: shell-quoted args,
 * trailing `\` line continuations, the dozen forms `-d / --data /
 * --data-binary / --data-raw / --data-urlencode` of body input, etc.
 *
 * We aim for the ~80% of real-world cURL commands you'll find on
 * Stack Overflow and DevTools' "Copy as cURL" output — not a
 * full POSIX shell parser. Specifically we DO handle:
 *
 *   - `curl <url>` prefix (optional)
 *   - line continuations: `\` followed by newline
 *   - single quotes, double quotes, unquoted
 *   - `-X / --request METHOD`
 *   - `-H / --header 'Name: value'` (repeatable)
 *   - `-d / --data / --data-binary / --data-raw <body>` (collected,
 *     joined with `&` if repeated)
 *   - `-u / --user user[:pass]`
 *   - `-F / --form name=value` (rendered as multipart in fetch
 *     output, urlencoded in axios/requests for portability — we
 *     emit a comment when we make this trade-off)
 *   - `-G / --get` (move body into query string)
 *   - `--url <url>` (alternate URL form)
 *
 * We do NOT handle: file uploads via `@filename`, `--cookie-jar`,
 * `--proxy`, `--cacert`, etc. They're surfaced as warnings.
 */

export interface ParsedRequest {
  method: string;
  url: string;
  headers: Record<string, string>;
  body?: string;
  /** Multipart fields parsed from -F. */
  form?: Array<{ name: string; value: string }>;
  auth?: { user: string; pass?: string };
  warnings: string[];
}

/* ------------------------------------------------------------------ */
/* Tokeniser                                                           */
/* ------------------------------------------------------------------ */

/**
 * Split a cURL command into argv-style tokens. Handles single /
 * double quotes and `\` line continuations. Within double quotes
 * we recognise `\"`, `\\`, `\n`, `\t`. Within single quotes
 * everything is literal (matches POSIX shell).
 */
export function tokenizeCurl(input: string): string[] {
  // Fold `\` + newline into a space.
  const folded = input.replace(/\\\r?\n/g, ' ').replace(/\r?\n/g, ' ');
  const tokens: string[] = [];
  let i = 0;
  while (i < folded.length) {
    const c = folded[i];
    if (c === ' ' || c === '\t') {
      i++;
      continue;
    }
    let token = '';
    while (i < folded.length) {
      const ch = folded[i];
      if (ch === ' ' || ch === '\t') break;
      if (ch === '\'') {
        // single-quoted: literal until next '
        const end = folded.indexOf('\'', i + 1);
        if (end === -1) {
          token += folded.slice(i + 1);
          i = folded.length;
          break;
        }
        token += folded.slice(i + 1, end);
        i = end + 1;
        continue;
      }
      if (ch === '"') {
        i++;
        while (i < folded.length && folded[i] !== '"') {
          if (folded[i] === '\\' && i + 1 < folded.length) {
            const next = folded[i + 1];
            if (next === '"' || next === '\\') {
              token += next;
              i += 2;
              continue;
            }
            if (next === 'n') {
              token += '\n';
              i += 2;
              continue;
            }
            if (next === 't') {
              token += '\t';
              i += 2;
              continue;
            }
            // Unknown escape → keep backslash literal.
            token += folded[i];
            i++;
            continue;
          }
          token += folded[i];
          i++;
        }
        if (i < folded.length) i++; // skip closing "
        continue;
      }
      // Unquoted: respect `\<space>` as literal space, otherwise
      // treat backslash before a non-meta char as literal too.
      if (ch === '\\' && i + 1 < folded.length) {
        token += folded[i + 1];
        i += 2;
        continue;
      }
      token += ch;
      i++;
    }
    tokens.push(token);
  }
  return tokens;
}

/* ------------------------------------------------------------------ */
/* Parser                                                              */
/* ------------------------------------------------------------------ */

const FLAGS_WITH_ARG = new Set([
  '-X',
  '--request',
  '-H',
  '--header',
  '-d',
  '--data',
  '--data-raw',
  '--data-binary',
  '--data-urlencode',
  '-u',
  '--user',
  '-F',
  '--form',
  '--url',
  '-A',
  '--user-agent',
  '-e',
  '--referer',
  '-b',
  '--cookie',
]);

const KNOWN_BOOLEAN_FLAGS = new Set([
  '-G',
  '--get',
  '-k',
  '--insecure',
  '-L',
  '--location',
  '--compressed',
  '-i',
  '--include',
  '-I',
  '--head',
  '-s',
  '--silent',
  '-v',
  '--verbose',
]);

export function parseCurl(input: string): ParsedRequest {
  const tokens = tokenizeCurl(input);
  // Drop a leading `curl` if present.
  if (tokens[0] === 'curl' || tokens[0] === '$' || tokens[0] === '$curl') {
    tokens.shift();
  }
  if (tokens[0] === 'curl') tokens.shift();

  const headers: Record<string, string> = {};
  const dataParts: string[] = [];
  const form: Array<{ name: string; value: string }> = [];
  const warnings: string[] = [];
  let method: string | null = null;
  let url = '';
  let auth: ParsedRequest['auth'];
  let isGet = false;

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];

    if (t === '-X' || t === '--request') {
      method = (tokens[++i] ?? 'GET').toUpperCase();
      continue;
    }
    if (t === '-H' || t === '--header') {
      const h = tokens[++i] ?? '';
      const colon = h.indexOf(':');
      if (colon !== -1) {
        const name = h.slice(0, colon).trim();
        const value = h.slice(colon + 1).trim();
        if (name) headers[name] = value;
      }
      continue;
    }
    if (
      t === '-d' ||
      t === '--data' ||
      t === '--data-raw' ||
      t === '--data-binary' ||
      t === '--data-urlencode'
    ) {
      const v = tokens[++i] ?? '';
      if (v.startsWith('@') && t !== '--data-raw') {
        warnings.push(`忽略文件输入：${t} ${v}（浏览器无法读取本地文件）`);
        continue;
      }
      dataParts.push(v);
      continue;
    }
    if (t === '-u' || t === '--user') {
      const v = tokens[++i] ?? '';
      const idx = v.indexOf(':');
      if (idx === -1) auth = { user: v };
      else auth = { user: v.slice(0, idx), pass: v.slice(idx + 1) };
      continue;
    }
    if (t === '-F' || t === '--form') {
      const v = tokens[++i] ?? '';
      const eq = v.indexOf('=');
      if (eq !== -1) {
        const name = v.slice(0, eq);
        const value = v.slice(eq + 1);
        if (value.startsWith('@')) {
          warnings.push(`忽略文件字段：${name}=${value}`);
        } else {
          form.push({ name, value });
        }
      }
      continue;
    }
    if (t === '-A' || t === '--user-agent') {
      headers['User-Agent'] = tokens[++i] ?? '';
      continue;
    }
    if (t === '-e' || t === '--referer') {
      headers['Referer'] = tokens[++i] ?? '';
      continue;
    }
    if (t === '-b' || t === '--cookie') {
      headers['Cookie'] = tokens[++i] ?? '';
      continue;
    }
    if (t === '--url') {
      url = tokens[++i] ?? '';
      continue;
    }
    if (t === '-G' || t === '--get') {
      isGet = true;
      continue;
    }
    if (KNOWN_BOOLEAN_FLAGS.has(t)) {
      continue;
    }
    if (t.startsWith('-')) {
      // Unknown flag — best-effort: assume single-arg flags need a follower.
      if (FLAGS_WITH_ARG.has(t)) {
        warnings.push(`未识别的 cURL 参数 ${t}（忽略）`);
        i++;
      } else {
        warnings.push(`未识别的 cURL 参数 ${t}（忽略，假定无参）`);
      }
      continue;
    }
    // Positional → URL.
    if (!url) url = t;
  }

  let body: string | undefined;
  if (dataParts.length > 0) {
    body = dataParts.join('&');
  }
  // -G: shift body into query string; verb is GET regardless of -X.
  if (isGet && body) {
    try {
      const u = new URL(url);
      const sp = new URLSearchParams(u.search);
      // body might already be a `key=val&key=val` string — feed it
      // through URLSearchParams to unify with existing params.
      const extras = new URLSearchParams(body);
      for (const [k, v] of extras) sp.append(k, v);
      u.search = sp.toString() ? `?${sp.toString()}` : '';
      url = u.toString();
      body = undefined;
    } catch {
      // url isn't fully formed yet (or relative). Concatenate naively.
      url += (url.includes('?') ? '&' : '?') + body;
      body = undefined;
    }
    method = method ?? 'GET';
  }

  if (!method) {
    if (body || form.length > 0) method = 'POST';
    else method = 'GET';
  }

  return {
    method,
    url,
    headers,
    body,
    form: form.length > 0 ? form : undefined,
    auth,
    warnings,
  };
}

/* ------------------------------------------------------------------ */
/* Generators                                                          */
/* ------------------------------------------------------------------ */

function jsLit(s: string): string {
  return JSON.stringify(s);
}

function indent(s: string, n = 2): string {
  return s.replace(/^/gm, ' '.repeat(n));
}

function authHeader(req: ParsedRequest): string | null {
  if (!req.auth) return null;
  const raw = `${req.auth.user}:${req.auth.pass ?? ''}`;
  // Prefer btoa (browser) then Buffer (Node). The output is just a
  // string so we can't generate code that conditionally picks one,
  // but btoa is ubiquitous in modern Node 18+ runtimes too.
  return `Basic ${typeof btoa !== 'undefined' ? btoa(raw) : Buffer.from(raw).toString('base64')}`;
}

export function toFetch(req: ParsedRequest): string {
  const headers = { ...req.headers };
  const ah = authHeader(req);
  if (ah) headers['Authorization'] = ah;

  const init: string[] = [];
  if (req.method && req.method !== 'GET') init.push(`method: ${jsLit(req.method)}`);
  if (Object.keys(headers).length > 0) {
    init.push(
      `headers: {\n${Object.entries(headers)
        .map(([k, v]) => `  ${jsLit(k)}: ${jsLit(v)}`)
        .join(',\n')}\n}`,
    );
  }
  if (req.form && req.form.length > 0) {
    const lines = req.form.map(
      (f) => `form.append(${jsLit(f.name)}, ${jsLit(f.value)});`,
    );
    init.push('body: form');
    return [
      `const form = new FormData();`,
      ...lines,
      ``,
      `await fetch(${jsLit(req.url)}, {`,
      indent(init.join(',\n')),
      `});`,
    ].join('\n');
  }
  if (req.body !== undefined) init.push(`body: ${jsLit(req.body)}`);

  if (init.length === 0) return `await fetch(${jsLit(req.url)});`;
  return `await fetch(${jsLit(req.url)}, {\n${indent(init.join(',\n'))}\n});`;
}

export function toAxios(req: ParsedRequest): string {
  const headers = { ...req.headers };
  const cfg: string[] = [];
  cfg.push(`url: ${jsLit(req.url)}`);
  cfg.push(`method: ${jsLit((req.method || 'GET').toLowerCase())}`);
  if (Object.keys(headers).length > 0) {
    cfg.push(
      `headers: {\n${Object.entries(headers)
        .map(([k, v]) => `  ${jsLit(k)}: ${jsLit(v)}`)
        .join(',\n')}\n}`,
    );
  }
  if (req.auth) {
    cfg.push(
      `auth: { username: ${jsLit(req.auth.user)}, password: ${jsLit(
        req.auth.pass ?? '',
      )} }`,
    );
  }
  if (req.form && req.form.length > 0) {
    const obj = req.form
      .map((f) => `  ${jsLit(f.name)}: ${jsLit(f.value)}`)
      .join(',\n');
    cfg.push(`data: new URLSearchParams({\n${obj}\n})`);
  } else if (req.body !== undefined) {
    cfg.push(`data: ${jsLit(req.body)}`);
  }
  return `await axios({\n${indent(cfg.join(',\n'))}\n});`;
}

function pyLit(s: string): string {
  // Python-string-literal: prefer single quotes, escape backslashes
  // and quotes.
  return `'${s.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}

export function toRequests(req: ParsedRequest): string {
  const lines: string[] = [`import requests`, ``];
  const args: string[] = [pyLit(req.url)];
  if (Object.keys(req.headers).length > 0) {
    args.push(
      `headers={\n${Object.entries(req.headers)
        .map(([k, v]) => `    ${pyLit(k)}: ${pyLit(v)}`)
        .join(',\n')}\n}`,
    );
  }
  if (req.auth) {
    args.push(
      `auth=(${pyLit(req.auth.user)}, ${pyLit(req.auth.pass ?? '')})`,
    );
  }
  if (req.form && req.form.length > 0) {
    args.push(
      `data={\n${req.form
        .map((f) => `    ${pyLit(f.name)}: ${pyLit(f.value)}`)
        .join(',\n')}\n}`,
    );
  } else if (req.body !== undefined) {
    args.push(`data=${pyLit(req.body)}`);
  }
  const fn = (req.method || 'GET').toLowerCase();
  if (fn === 'get' || fn === 'post' || fn === 'put' || fn === 'delete' || fn === 'patch') {
    lines.push(`r = requests.${fn}(\n${indent(args.join(',\n'), 4)}\n)`);
  } else {
    lines.push(
      `r = requests.request(${pyLit(req.method)},\n${indent(args.join(',\n'), 4)}\n)`,
    );
  }
  lines.push(`r.raise_for_status()`);
  lines.push(`print(r.text)`);
  return lines.join('\n');
}

function shellQuote(s: string): string {
  // Single-quote-safe shell escape: close, escape, reopen.
  return `'${s.replace(/'/g, "'\\''")}'`;
}

export function toWget(req: ParsedRequest): string {
  const parts: string[] = ['wget'];
  const method = (req.method || 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'POST') {
    parts.push(`--method=${shellQuote(method)}`);
  }
  for (const [k, v] of Object.entries(req.headers)) {
    parts.push(`--header=${shellQuote(`${k}: ${v}`)}`);
  }
  if (req.auth) {
    parts.push(`--user=${shellQuote(req.auth.user)}`);
    if (req.auth.pass) parts.push(`--password=${shellQuote(req.auth.pass)}`);
  }
  if (req.form && req.form.length > 0) {
    const body = req.form
      .map((f) => `${encodeURIComponent(f.name)}=${encodeURIComponent(f.value)}`)
      .join('&');
    parts.push(`--post-data=${shellQuote(body)}`);
  } else if (req.body !== undefined) {
    parts.push(method === 'POST' ? `--post-data=${shellQuote(req.body)}` : `--body-data=${shellQuote(req.body)}`);
  }
  parts.push('--quiet', '-O', '-');
  parts.push(shellQuote(req.url));
  return parts.join(' \\\n  ');
}
